import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ChevronLeft, Youtube, DollarSign, GraduationCap, TrendingUp, Loader2 } from 'lucide-react';
import { getCareerBySlug } from '../services/careerService';
import { PLACEHOLDER_IMAGES, SITE_URL, toPublicUrl } from '../constants';
import { Career } from '../types';
import Button from '../components/Button';
import SEO from '../components/SEO';

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="text-2xl font-heading font-semibold text-brand-white mb-6 flex items-center">
    <div className="w-1 h-6 bg-brand-cyan rounded-full mr-3"></div>
    {children}
  </h2>
);

const CareerDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [career, setCareer] = useState<Career | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const [heroIndex, setHeroIndex] = useState(0);
  const [sidebarIndex, setSidebarIndex] = useState(0);

  const v = (import.meta as any).env?.VITE_BUILD_ID || '';
  const withV = (u: string) => (u && v ? `${u}${u.includes('?') ? '&' : '?'}v=${v}` : u);

  useEffect(() => {
    setIsLoading(true);
    if (slug) {
      getCareerBySlug(slug).then(data => {
        setCareer(data || null);
        setIsLoading(false);
      });
    }
  }, [slug]);

  const heroSources = useMemo(() => [
    toPublicUrl(career?.images?.hero),
    toPublicUrl(PLACEHOLDER_IMAGES.hero),
    `https://placehold.co/1200x675/0B2A33/FFFFFF?text=${encodeURIComponent(career?.title || '')}`
  ].filter(Boolean) as string[], [career]);

  const sidebarSources = useMemo(() => [
    toPublicUrl(career?.images?.sidebar),
    toPublicUrl(career?.images?.card),
    toPublicUrl(PLACEHOLDER_IMAGES.sidebar),
  ].filter(Boolean) as string[], [career]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-brand-cyan animate-spin" />
      </div>
    );
  }

  if (!career) return <Navigate to="/careers" replace />;

  const currentHero = withV(heroSources[heroIndex]);
  const ogImage = currentHero.startsWith('http') 
    ? currentHero 
    : `${SITE_URL}${currentHero}`;

  return (
    <div className="min-h-screen bg-brand-bg pb-20">
      <SEO title={career.title} description={career.tagline} image={ogImage} />
      
      <div className="relative h-[50vh] min-h-[400px] w-full bg-brand-dark overflow-hidden">
        <div className="absolute inset-0">
          <img 
            key={`hero-${heroIndex}`}
            src={currentHero} 
            alt={career.title} 
            className="w-full h-full object-cover opacity-60"
            onError={() => heroIndex < heroSources.length - 1 && setHeroIndex(i => i + 1)}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/80 to-transparent"></div>
        </div>

        <div className="absolute inset-0 flex flex-col justify-end pb-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <Link to="/careers" className="inline-flex items-center text-brand-grey2 hover:text-brand-cyan mb-6 transition-colors">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to Careers
            </Link>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-brand-cyan/10 border border-brand-cyan/20 text-brand-cyan text-xs font-semibold uppercase tracking-wider mb-4">
                  {career.category}
                </span>
                <h1 className="text-4xl md:text-6xl font-heading font-bold text-brand-white mb-4">
                  {career.title} <span className="text-4xl ml-2">{career.emoji}</span>
                </h1>
                <p className="text-xl text-brand-grey1 max-w-2xl font-light">{career.tagline}</p>
              </div>
              <div className="flex-shrink-0">
                <a href={career.video.youtubeUrl} target="_blank" rel="noreferrer">
                  <Button size="lg" className="shadow-xl shadow-brand-cyan/20 animate-pulse-slow">
                    <Youtube className="w-5 h-5 mr-2" />
                    {career.video.ctaText || "Watch Short"}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          <div className="bg-brand-light/90 backdrop-blur border border-brand-border p-6 rounded-xl text-center">
            <DollarSign className="w-5 h-5 mx-auto text-brand-cyan mb-2" />
            <p className="text-brand-white font-medium text-lg">{career.salaryRange || "Variable"}</p>
          </div>
          <div className="bg-brand-light/90 backdrop-blur border border-brand-border p-6 rounded-xl text-center">
            <GraduationCap className="w-5 h-5 mx-auto text-brand-green mb-2" />
            <p className="text-brand-white font-medium text-lg">{career.education[0]}</p>
          </div>
          <div className="bg-brand-light/90 backdrop-blur border border-brand-border p-6 rounded-xl text-center">
            <TrendingUp className="w-5 h-5 mx-auto text-purple-400 mb-2" />
            <p className="text-brand-white font-medium text-lg">Growth Path</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <section>
              <SectionTitle>What You Do</SectionTitle>
              <div className="space-y-4">
                {career.whatTheyDo.map((item, idx) => (
                  <div key={idx} className="bg-brand-light p-4 rounded-xl border border-brand-border/50 flex gap-4">
                     <span className="w-6 h-6 rounded-full bg-brand-cyan/20 flex items-center justify-center text-brand-cyan text-xs font-bold">{idx + 1}</span>
                     <p className="text-brand-grey1">{item}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
          <div className="lg:col-span-1">
             <div className="aspect-[9/16] relative bg-brand-light rounded-2xl overflow-hidden border border-brand-border">
                <img 
                  src={withV(sidebarSources[sidebarIndex])} 
                  className="w-full h-full object-cover" 
                  onError={() => sidebarIndex < sidebarSources.length - 1 && setSidebarIndex(i => i + 1)}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <a href={career.video.youtubeUrl} target="_blank" rel="noreferrer" className="w-16 h-16 bg-brand-cyan rounded-full flex items-center justify-center text-brand-bg shadow-xl">
                    <Youtube className="w-8 h-8 fill-current" />
                  </a>
                </div>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareerDetailPage;
